<template>
  <div class="inventory-page">
    <b-alert
      v-if="reminder.overdue"
      show
      variant="warning"
      class="mb-2"
    >
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <strong>Inventaire en retard</strong>
          <span class="ml-1">Un ou plusieurs dépôts n'ont pas clôturé l'inventaire du jour avant {{ reminder.due_time }}.</span>
        </div>
        <b-button
          size="sm"
          variant="outline-warning"
          @click="fetchReminderStatus"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
          />
        </b-button>
      </div>
    </b-alert>

    <b-card class="mb-2">
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="5"
        >
          <b-form-group
            v-if="isAdmin"
            label="Dépôt"
          >
            <v-select
              v-model="selectedRepository"
              :options="repositories"
              label="name"
              :clearable="false"
              placeholder="Choisir un dépôt"
              @input="handleRepositoryChange"
            />
          </b-form-group>
          <b-form-group
            v-else
            label="Dépôt"
          >
            <b-form-input
              :value="selectedRepository ? selectedRepository.name : ''"
              disabled
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="7"
          class="text-md-right"
        >
          <b-button
            variant="primary"
            class="mr-1 mb-1"
            :disabled="!selectedRepository || loading || Boolean(activeSession)"
            @click="openSession"
          >
            <feather-icon
              icon="PlayCircleIcon"
              size="15"
              class="mr-50"
            />
            Ouvrir inventaire
          </b-button>
          <b-button
            variant="success"
            class="mb-1"
            :disabled="!activeSession || loading"
            @click="closeSession"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="15"
              class="mr-50"
            />
            Clôturer
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-row>
      <b-col
        v-for="metric in metrics"
        :key="metric.key"
        cols="6"
        md="3"
      >
        <b-card class="metric-card">
          <small class="text-muted">{{ metric.label }}</small>
          <h2 class="mb-0">
            {{ metric.value }}
          </h2>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      title="Synchronisation par statut"
      class="mb-2"
    >
      <b-table
        small
        responsive
        :items="statusSyncRows"
        :fields="statusSyncFields"
        :busy="loading"
        empty-text="Aucun colis à suivre"
        show-empty
      >
        <template #cell(not_synchronized)="data">
          <b-button
            size="sm"
            :variant="data.value > 0 ? 'outline-danger' : 'outline-success'"
            :disabled="data.value === 0"
            @click="openStatusSyncDetails(data.item, false)"
          >
            {{ data.value }}
          </b-button>
        </template>
        <template #cell(synchronized)="data">
          <b-button
            size="sm"
            variant="outline-success"
            :disabled="data.value === 0"
            @click="openStatusSyncDetails(data.item, true)"
          >
            {{ data.value }}
          </b-button>
        </template>
        <template #cell(scanned)="data">
          <span>{{ data.item.scanned }}</span>
          <small class="text-muted"> / {{ data.item.total }}</small>
        </template>
        <template #cell(remaining)="data">
          <b-badge :variant="data.value > 0 ? 'warning' : 'light-success'">
            {{ data.value }}
          </b-badge>
        </template>
      </b-table>
    </b-card>

    <b-card class="mb-2">
      <b-form-group label="Scanner colis">
        <b-input-group>
          <b-input-group-prepend is-text>
            <feather-icon icon="MaximizeIcon" />
          </b-input-group-prepend>
          <b-form-input
            ref="scanInput"
            v-model="scanRef"
            :disabled="!activeSession || loading"
            placeholder="Saisir ou scanner la référence du colis"
            @change="scanOrder"
          />
          <b-input-group-append>
            <b-button
              variant="primary"
              :disabled="!activeSession || loading || !scanRef"
              @click="scanOrder"
            >
              <feather-icon
                icon="CheckIcon"
                size="15"
              />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-alert
        v-if="lastScan.message"
        show
        :variant="lastScan.variant"
        class="mb-0"
      >
        {{ lastScan.message }}
      </b-alert>
    </b-card>

    <b-row>
      <b-col
        cols="12"
        lg="7"
      >
        <b-card title="Scans">
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="scanFilter"
                placeholder="Filtrer par référence, colis, external ID ou téléphone"
              />
            </b-input-group>
          </b-form-group>
          <b-table
            small
            responsive
            :items="filteredScans"
            :fields="scanFields"
            :busy="loading"
            empty-text="Aucun scan"
            show-empty
          >
            <template #cell(scan_result)="data">
              <b-badge :variant="scanVariant(data.item.scan_result)">
                {{ scanResultDisplay(data.item) }}
              </b-badge>
            </template>
            <template #cell(current_status)="data">
              <span>{{ currentStatusDisplay(data.item) }}</span>
            </template>
            <template #cell(sync_status)="data">
              <b-badge :variant="syncStatusVariant(data.item)">
                {{ syncStatusDisplay(data.item) }}
              </b-badge>
            </template>
            <template #cell(order)="data">
              <span v-if="data.item.order">{{ displayOrderRef(data.item.order) }}</span>
              <span v-else>-</span>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        lg="5"
      >
        <b-card title="Colis non scannés">
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="missingFilter"
                placeholder="Filtrer par colis, external ID ou téléphone"
              />
            </b-input-group>
          </b-form-group>
          <b-table
            small
            responsive
            :items="filteredMissingOrders"
            :fields="missingFields"
            :busy="loading"
            empty-text="Aucun colis manquant"
            show-empty
          >
            <template #cell(order)="data">
              {{ displayOrderRef(data.item.order) }}
            </template>
            <template #cell(customer)="data">
              <div>{{ data.item.order.name }}</div>
              <small class="text-muted">{{ data.item.order.phone1 }}</small>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      id="inventory-status-orders-modal"
      :title="statusOrdersModalTitle"
      size="xl"
      ok-only
      ok-title="Fermer"
    >
      <b-table
        small
        responsive
        :items="statusOrders"
        :fields="statusOrderFields"
        :busy="statusOrdersLoading"
        empty-text="Aucun colis"
        show-empty
      >
        <template #cell(order)="data">
          {{ displayOrderRef(data.item) }}
        </template>
        <template #cell(customer)="data">
          <div>{{ data.item.name }}</div>
          <small class="text-muted">{{ data.item.phone1 }}</small>
        </template>
        <template #cell(sync)="data">
          <b-badge :variant="data.item.is_synchronized ? 'success' : 'danger'">
            {{ data.item.is_synchronized ? 'Synchronisé' : 'Non synchronisé' }}
          </b-badge>
        </template>
      </b-table>
    </b-modal>

  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BModal,
  BRow,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BModal,
    BRow,
    BTable,
    vSelect,
  },
  data() {
    return {
      user: {},
      repositories: [],
      selectedRepository: null,
      summary: {
        total: 0,
        colis_au_depot: 0,
        retour_au_depot: 0,
        status_sync_breakdown: [],
      },
      activeSession: null,
      viewedSession: null,
      scans: [],
      scanFilter: '',
      missingOrders: [],
      missingFilter: '',
      scanRef: '',
      loading: false,
      reminder: {
        overdue: false,
        due_time: '18:00',
      },
      lastScan: {
        message: '',
        variant: 'info',
      },
      statusOrders: [],
      statusOrdersLoading: false,
      statusOrdersModalTitle: 'Colis',
      scanFields: [
        { key: 'scanned_ref', label: 'Référence scannée' },
        { key: 'order', label: 'Colis' },
        { key: 'scan_result', label: 'Résultat' },
        { key: 'current_status', label: 'Statut actuel' },
        { key: 'sync_status', label: 'Sync' },
        { key: 'message', label: 'Message' },
      ],
      statusSyncFields: [
        { key: 'label', label: 'Statut' },
        { key: 'total', label: 'Total' },
        { key: 'synchronized', label: 'Synchronisés' },
        { key: 'not_synchronized', label: 'Non synchronisés' },
        { key: 'scanned', label: 'Scannés' },
        { key: 'remaining', label: 'Restants' },
      ],
      statusOrderFields: [
        { key: 'order', label: 'Colis' },
        { key: 'external_id', label: 'External ID' },
        { key: 'customer', label: 'Client' },
        { key: 'status_display', label: 'Statut' },
        { key: 'sync', label: 'Sync' },
      ],
      missingFields: [
        { key: 'order', label: 'Colis' },
        { key: 'expected_status_display', label: 'Statut attendu' },
        { key: 'customer', label: 'Client' },
      ],
    }
  },
  computed: {
    isAdmin() {
      return ['admin', 'superadmin'].includes(this.user.role)
    },
    currentCounts() {
      return this.viewedSession || this.summary
    },
    metrics() {
      const counts = this.currentCounts
      const expectedTotal = counts.expected_total !== undefined ? counts.expected_total : (counts.total || 0)
      const scannedTotal = counts.scanned_total || 0

      return [
        {
          key: 'expected',
          label: 'Total attendu',
          value: expectedTotal,
        },
        {
          key: 'depot',
          label: 'Colis au dépôt',
          value: counts.expected_colis_au_depot !== undefined ? counts.expected_colis_au_depot : (counts.colis_au_depot || 0),
        },
        {
          key: 'return',
          label: 'Retour au dépôt',
          value: counts.expected_retour_au_depot !== undefined ? counts.expected_retour_au_depot : (counts.retour_au_depot || 0),
        },
        {
          key: 'remaining',
          label: 'Restants',
          value: Math.max(expectedTotal - scannedTotal, 0),
        },
      ]
    },
    statusSyncRows() {
      const counts = this.currentCounts || {}
      return counts.status_sync_breakdown || []
    },
    filteredMissingOrders() {
      const search = String(this.missingFilter || '').trim().toLowerCase()
      if (!search) return this.missingOrders

      return this.missingOrders.filter(item => {
        const order = item.order || {}
        const values = [
          order.id,
          order.external_id,
          order.phone1,
          order.phone2,
        ]

        return values.some(value => String(value || '').toLowerCase().includes(search))
      })
    },
    filteredScans() {
      const search = String(this.scanFilter || '').trim().toLowerCase()
      if (!search) return this.scans

      return this.scans.filter(scan => {
        const order = scan.order || {}
        const values = [
          scan.scanned_ref,
          order.id,
          order.external_id,
          order.phone1,
          order.phone2,
        ]

        return values.some(value => String(value || '').toLowerCase().includes(search))
      })
    },
  },
  async created() {
    this.user = storeAuth.state.user || {}
    await this.initializeRepository()
    if (this.$route.query.session) {
      await this.loadSession(this.$route.query.session)
    }
    await this.fetchReminderStatus()
  },
  methods: {
    async initializeRepository() {
      if (this.isAdmin) {
        await this.fetchRepositories()
        if (this.repositories.length > 0) {
          const [firstRepository] = this.repositories
          this.selectedRepository = firstRepository
          await this.handleRepositoryChange()
        }
      } else {
        const repository = this.normalizeUserRepository()
        if (repository) {
          this.selectedRepository = repository
          await this.handleRepositoryChange()
        }
      }
    },
    normalizeUserRepository() {
      if (!this.user.repository) return null
      if (typeof this.user.repository === 'object') return this.user.repository
      return {
        id: this.user.repository,
        name: this.user.repository_name || `Dépôt ${this.user.repository}`,
      }
    },
    async fetchRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    async handleRepositoryChange() {
      await this.fetchSummary()
      if (this.summary.open_session_id) {
        await this.loadSession(this.summary.open_session_id)
      } else {
        this.activeSession = null
        this.viewedSession = null
        this.scans = []
        this.missingOrders = []
      }
      this.focusScanner()
    },
    async fetchSummary() {
      if (!this.selectedRepository) return
      const { data } = await axios.get('/api/inventory/summary/', {
        params: { repository: this.selectedRepository.id },
      })
      this.summary = data
    },
    async fetchReminderStatus() {
      if (!this.isAdmin) return
      try {
        const { data } = await axios.get('/api/inventory/reminder-status/')
        this.reminder = data
      } catch (error) {
        this.reminder.overdue = false
      }
    },
    async openSession() {
      if (!this.selectedRepository) return
      this.loading = true
      try {
        const { data } = await axios.post('/api/inventory/sessions/', {
          repository: this.selectedRepository.id,
        })
        this.applySession(data)
        this.showToast('success', 'Inventaire ouvert')
      } catch (error) {
        this.showToast('danger', this.errorMessage(error))
      } finally {
        this.loading = false
        this.focusScanner()
      }
    },
    async scanOrder() {
      const ref = String(this.scanRef || '').trim()
      if (!ref || !this.activeSession || this.loading) return
      this.loading = true
      try {
        const { data } = await axios.post(`/api/inventory/sessions/${this.activeSession.id}/scan/`, { ref })
        this.activeSession = data.session
        this.viewedSession = data.session
        this.scans.unshift(data.scan)
        this.scanRef = ''
        this.lastScan = {
          message: data.scan.message,
          variant: this.scanVariant(data.scan.scan_result),
        }
      } catch (error) {
        this.lastScan = {
          message: this.errorMessage(error),
          variant: 'danger',
        }
      } finally {
        this.loading = false
        this.focusScanner()
      }
    },
    async openStatusSyncDetails(row, synchronized) {
      if (!this.selectedRepository || !row) return
      this.statusOrders = []
      this.statusOrdersLoading = true
      const syncLabel = synchronized ? 'synchronisés' : 'non synchronisés'
      this.statusOrdersModalTitle = `${row.label} - colis ${syncLabel}`
      this.$bvModal.show('inventory-status-orders-modal')
      try {
        const params = {
          repository: this.selectedRepository.id,
          status: row.status,
          synchronized,
        }
        if (this.viewedSession && this.viewedSession.id) {
          params.session = this.viewedSession.id
        }
        const { data } = await axios.get('/api/inventory/status-orders/', { params })
        this.statusOrders = data.orders || []
      } catch (error) {
        this.showToast('danger', this.errorMessage(error))
      } finally {
        this.statusOrdersLoading = false
      }
    },
    async closeSession() {
      if (!this.activeSession) return
      const result = await this.$swal({
        title: 'Clôturer inventaire',
        text: 'Les colis non scannés seront enregistrés comme manquants.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Clôturer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return

      this.loading = true
      try {
        const { data } = await axios.post(`/api/inventory/sessions/${this.activeSession.id}/close/`, {})
        this.applySession(data)
        await this.fetchSummary()
        await this.fetchReminderStatus()
        this.showToast('success', 'Inventaire clôturé')
      } catch (error) {
        this.showToast('danger', this.errorMessage(error))
      } finally {
        this.loading = false
      }
    },
    async loadSession(id) {
      this.loading = true
      try {
        const { data } = await axios.get(`/api/inventory/sessions/${id}/`)
        this.applySession(data)
      } catch (error) {
        this.showToast('danger', this.errorMessage(error))
      } finally {
        this.loading = false
        this.focusScanner()
      }
    },
    applySession(session) {
      this.viewedSession = session
      this.activeSession = session.status === 'open' ? session : null
      if (session.repository) {
        this.selectedRepository = session.repository
      }
      this.scans = session.scans || []
      this.missingOrders = session.missing_orders || []
    },
    displayOrderRef(order) {
      if (!order) return '-'
      if (order.display_cross_tenant_barcode && order.cross_tenant_barcode) {
        return order.cross_tenant_barcode
      }
      return order.id
    },
    scanLabel(result) {
      const labels = {
        matched: 'Scanné',
        duplicate: 'Doublon',
        wrong_status: 'Statut incorrect',
        wrong_depot: 'Autre dépôt',
        not_found: 'Introuvable',
      }
      return labels[result] || result
    },
    scanResultDisplay(scan) {
      if (scan && scan.scan_result === 'wrong_status') {
        return this.currentStatusDisplay(scan)
      }
      return this.scanLabel(scan ? scan.scan_result : '')
    },
    scanVariant(result) {
      const variants = {
        matched: 'success',
        duplicate: 'warning',
        wrong_status: 'danger',
        wrong_depot: 'danger',
        not_found: 'dark',
      }
      return variants[result] || 'secondary'
    },
    currentStatusDisplay(scan) {
      if (!scan) return '-'
      if (scan.order_status_display_at_scan) return scan.order_status_display_at_scan
      if (scan.order && scan.order.status_display) return scan.order.status_display
      return '-'
    },
    syncStatusDisplay(scan) {
      if (!scan || !scan.order) return '-'
      if (scan.sync_status_display) return scan.sync_status_display
      return scan.is_synchronized || scan.order.is_synchronized ? 'Synchronisé' : 'Non synchronisé'
    },
    syncStatusVariant(scan) {
      if (!scan || !scan.order) return 'secondary'
      return scan.is_synchronized || scan.order.is_synchronized ? 'success' : 'danger'
    },
    focusScanner() {
      this.$nextTick(() => {
        if (this.$refs.scanInput && this.activeSession) {
          this.$refs.scanInput.focus()
        }
      })
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Inventaire',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
    errorMessage(error) {
      if (error.response && error.response.data) {
        return error.response.data.error || error.response.data.detail || error.toString()
      }
      return error.toString()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.inventory-page {
  .metric-card {
    min-height: 96px;
  }
}
</style>
